import { FirebaseError } from 'firebase/app';

import { inject, Injectable } from '@angular/core';
import {
	Auth,
	createUserWithEmailAndPassword,
	sendEmailVerification,
	sendPasswordResetEmail,
	signInWithEmailAndPassword,
	updatePassword,
	updateProfile,
	UserCredential,
} from '@angular/fire/auth';
import { Timestamp } from '@angular/fire/firestore';
import { IUserEmailLogin } from '@auth/models/login';
import { IPasswordReset } from '@auth/models/password-reset';
import { IUserEmailSignUp } from '@auth/models/signup';
import { AnalyticEvent } from '@shared/models/analytics';
import { ToastTypes } from '@shared/models/toast';
import { AnalyticsService } from '@shared/services/analytics/analytics.service';
import { ToastService } from '@shared/services/toast/toast.service';
import { DEFAULT_USER_SETTINGS } from '@user/models/user';
import { UsersService } from '@user/services/users/users.service';

import { AuthToastService } from '../auth-toast/auth-toast.service';

@Injectable({
	providedIn: 'root',
})
export class EmailAuthService {
	private auth = inject(Auth);
	private usersService = inject(UsersService);
	private analyticsService = inject(AnalyticsService);
	private toastAuthService = inject(AuthToastService);
	private toastService = inject(ToastService);

	async signUpWithEmailAndPassword(userSignup: IUserEmailSignUp): Promise<UserCredential | void> {
		try {
			const result = await createUserWithEmailAndPassword(this.auth, userSignup.email, userSignup.password);
			await sendEmailVerification(result.user);
			await updateProfile(result.user, { displayName: userSignup.fullName });
			await this.usersService.set(result.user.uid, {
				uid: result.user.uid,
				email: result.user.email,
				displayName: userSignup.fullName,
				photoUrl: result.user.photoURL || null,
				phoneNumber: {
					e164Number: '',
					countryCode: '',
					nationalNumber: '',
					dialCode: '',
					internationalNumber: '',
					number: '',
				},
				settings: DEFAULT_USER_SETTINGS,
				createdAt: result.user.metadata.creationTime
					? Timestamp.fromDate(new Date(result.user.metadata.creationTime))
					: Timestamp.now(),
				updatedAt: Timestamp.now(),
			});

			this.analyticsService.logEvent(AnalyticEvent.SIGN_UP);
			return result;
		} catch (error: unknown) {
			console.error(error);
			if (error instanceof FirebaseError) {
				this.toastAuthService.handleError('Sign up Error', error.code);
			}
		}
	}

	async signInWithEmailAndPassword(user: IUserEmailLogin): Promise<void> {
		try {
			await signInWithEmailAndPassword(this.auth, user.email, user.password);
			this.analyticsService.logEvent(AnalyticEvent.LOGIN);
		} catch (error: unknown) {
			console.error(error);
			if (error instanceof FirebaseError) {
				this.toastAuthService.handleError('Login Error', error.code);
			}
		}
	}

	async updatePassword(newPassword: string): Promise<void> {
		try {
			await updatePassword(this.auth.currentUser!, newPassword);
			this.analyticsService.logEvent(AnalyticEvent.PASSWORD_CHANGE);
		} catch (error: unknown) {
			console.error(error);
			if (error instanceof FirebaseError) {
				this.toastAuthService.handleError('Password Change Error', error.code);
			}
			throw error;
		}
	}

	async sendPasswordResetEmail(passwordReset: IPasswordReset): Promise<void> {
		try {
			await sendPasswordResetEmail(this.auth, passwordReset.email);
			this.toastService.showToast({
				title: 'Password Reset',
				message: 'An email has been sent, please click on the link in the email to reset your password.',
				type: ToastTypes.SUCCESS,
			});
			this.analyticsService.logEvent(AnalyticEvent.PASSWORD_RESET);
		} catch (error: unknown) {
			console.error(error);
			if (error instanceof FirebaseError) {
				this.toastAuthService.handleError('Password Reset Error', error.code);
			}
		}
	}

	async sendEmailVerification(): Promise<void> {
		try {
			await sendEmailVerification(this.auth.currentUser!);
			this.toastService.showToast({
				title: 'Email Verification',
				message: 'An email has been sent, please click on the link in the email to verify your email.',
				type: ToastTypes.SUCCESS,
			});
			this.analyticsService.logEvent(AnalyticEvent.PASSWORD_RESET);
		} catch (error: unknown) {
			console.error(error);
			if (error instanceof FirebaseError) {
				this.toastAuthService.handleError('Email Verification Error', error.code);
			}
		}
	}
}
